import {
  FC,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useConversationHub } from "../../../hooks/useConversationHub";
import ConversationLanguageButton from "./ConversationLanguageButton";
import { ConversationLanguage } from "../../../models/conversation/Message";
import ConnectionStatus from "./ConnectionStatus";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { useContent } from "@/hooks/useContent";
import PromptLibraryDialog from "./dialogs/PromptLibraryDialog";
import PromptLibraryIcon from "./prompt-library/PromptLibraryIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import LawDictionaryModal from "./law-dictionary/LawDictionaryModal";
import ChainOfThoughtButton from "./ChainOfThoughtButton";
import ToneOfVoicePopover from "./ToneOfVoicePopover";
import { ArrowUp, Loader2 } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import Conversation from "@/models/conversation/Conversation";
import SelectedContext from "./SelectedContext";
import Workspace from "@/models/workspace/Workspace";
import useSynonymGroups from "@/hooks/useSynonymGroups";
import useUserPreference from "@/hooks/useUserPreference";
import {
  buildSynonymDictionary,
  findCurrentWord,
  findSynonymMatches,
  SynonymMatch,
} from "@/lib/synonymMatching";
import {
  clampSynonymPopoverToViewport,
  estimateSynonymPopoverHeight,
} from "@/lib/clampSynonymPopoverToViewport";
import SynonymSuggestions from "./SynonymSuggestions";
import { revealTextareaEnd } from "@/lib/revealTextareaEnd";
import debounce from "lodash/debounce";

type Props = {
  onSubmit: (text: string) => void;
  submitTestId?: string;
  inputTestId?: string;
  disabled: boolean;
  selectedConversation: Conversation | undefined;
  setInputValueRef?: React.MutableRefObject<((value: string) => void) | null>;
  workspaces?: Workspace[];
  isLoadingWorkspaces?: boolean;
  onPendingSubmit?: (text: string) => boolean; // Returns true if submission is pending (should not clear input)
  /** True while the PII scan runs — shows a spinner on the send button (LAR-27 v2). */
  isScanning?: boolean;
};

const DEFAULT_INPUT_ROWS = 1;
const MAX_INPUT_ROWS = 10;

const ChatInput: FC<Props> = ({
  onSubmit,
  submitTestId,
  inputTestId,
  disabled,
  selectedConversation,
  setInputValueRef,
  workspaces,
  isLoadingWorkspaces,
  onPendingSubmit,
  isScanning,
}) => {
  // Hooks
  const { useQueryUserDictionary } = useSynonymGroups();
  const { useQuerySynonymPreferences } = useUserPreference();
  const {
    waitingForResponse,
    selectedLanguage,
    setSelectedLanguage,
    connectionState,
    manualReconnect,
    useChainOfThought,
    setUseChainOfThought,
  } = useConversationHub();
  const { t } = useContent();

  // Synonym matching queries
  const userDictionaryQuery = useQueryUserDictionary();
  const synonymPreferencesQuery = useQuerySynonymPreferences();

  // State
  const [value, setValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [synonymMatches, setSynonymMatches] = useState<SynonymMatch[]>([]);
  const [matchPosition, setMatchPosition] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  // Bumped every time text arrives from outside the input (a saved prompt from
  // the Prompt Library), which asks the layout effect below to reveal its end.
  const [revealEndRequest, setRevealEndRequest] = useState(0);
  const [suggestionPosition, setSuggestionPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Refs
  const chatInputRef = useRef<HTMLDivElement>(
    null
  ) as RefObject<HTMLDivElement>;
  const textareaRef = useRef<HTMLTextAreaElement>(
    null
  ) as RefObject<HTMLTextAreaElement>;
  const textContainerRef = useRef<HTMLDivElement>(
    null
  ) as RefObject<HTMLDivElement>;

  /**
   * Fills the input with text that came from somewhere else — today the Prompt
   * Library, via `setInputValueRef` or the dialog below. A prompt is often
   * taller than MAX_INPUT_ROWS, so the end of it is scrolled into view with the
   * caret there, ready to be added to (LAR-128).
   */
  const setInputValue = useCallback((text: string) => {
    setValue(text);
    if (text) {
      setCursorPosition(text.length);
      // A counter rather than a flag: re-applying the same prompt leaves the
      // value untouched, and the end still has to be revealed.
      setRevealEndRequest((request) => request + 1);
    }
  }, []);

  // Expose the setter via ref if provided
  useEffect(() => {
    if (setInputValueRef) {
      setInputValueRef.current = setInputValue;
    }
  }, [setInputValueRef, setInputValue]);

  // Build synonym dictionary from fetched data
  const synonymDictionary = useMemo(() => {
    return buildSynonymDictionary(
      userDictionaryQuery.data,
      synonymPreferencesQuery.data
    );
  }, [userDictionaryQuery.data, synonymPreferencesQuery.data]);
  // Calculate popover position based on cursor position using Range API
  const calculateSuggestionPosition = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !matchPosition) {
      return null;
    }

    try {
      // Since textarea doesn't support Range API directly, create a mirror div
      // that matches the textarea styling exactly and use Range API on it
      const textareaStyle = window.getComputedStyle(textarea);
      const textareaRect = textarea.getBoundingClientRect();
      const paddingTop = parseFloat(textareaStyle.paddingTop) || 0;
      const paddingLeft = parseFloat(textareaStyle.paddingLeft) || 0;

      // Create a mirror div that matches textarea exactly
      const mirrorDiv = document.createElement("div");
      mirrorDiv.style.position = "absolute";
      mirrorDiv.style.visibility = "hidden";
      mirrorDiv.style.whiteSpace = "pre-wrap";
      mirrorDiv.style.wordWrap = "break-word";
      mirrorDiv.style.overflowWrap = "break-word";
      mirrorDiv.style.font = textareaStyle.font;
      mirrorDiv.style.fontSize = textareaStyle.fontSize;
      mirrorDiv.style.fontFamily = textareaStyle.fontFamily;
      mirrorDiv.style.fontWeight = textareaStyle.fontWeight;
      mirrorDiv.style.lineHeight = textareaStyle.lineHeight;
      mirrorDiv.style.letterSpacing = textareaStyle.letterSpacing;
      mirrorDiv.style.padding = textareaStyle.padding;
      mirrorDiv.style.border = textareaStyle.border;
      mirrorDiv.style.boxSizing = textareaStyle.boxSizing;
      mirrorDiv.style.width = `${textarea.offsetWidth}px`;

      // Position mirror div exactly where textarea's content area starts
      mirrorDiv.style.top = `${textareaRect.top + paddingTop}px`;
      mirrorDiv.style.left = `${textareaRect.left + paddingLeft}px`;

      // Insert text with a span around the matched word to get its exact position
      const textBefore = value.slice(0, matchPosition.start);
      const matchedWord = value.slice(matchPosition.start, matchPosition.end);
      const textAfter = value.slice(matchPosition.end);

      // Create text nodes and span for the matched word
      const beforeNode = document.createTextNode(textBefore);
      const wordSpan = document.createElement("span");
      wordSpan.textContent = matchedWord;
      const afterNode = document.createTextNode(textAfter);

      mirrorDiv.appendChild(beforeNode);
      mirrorDiv.appendChild(wordSpan);
      mirrorDiv.appendChild(afterNode);

      document.body.appendChild(mirrorDiv);

      // Get the bounding rectangle of the word span (already in viewport coordinates)
      const wordRect = wordSpan.getBoundingClientRect();

      // Clean up
      document.body.removeChild(mirrorDiv);

      // Position popover below the word; clamp so fixed PopoverContent stays in viewport
      const raw = {
        top: wordRect.bottom + 4,
        left: wordRect.left,
      };
      const anchor = {
        top: wordRect.top,
        left: wordRect.left,
        bottom: wordRect.bottom,
        right: wordRect.right,
      };
      return clampSynonymPopoverToViewport(raw, anchor, {
        maxPopoverHeight: estimateSynonymPopoverHeight(synonymMatches.length),
      });
    } catch (error) {
      // Fallback if Range API fails
      logger.warn("Failed to calculate synonym suggestion position", {
        operation: "positionChatSynonymSuggestions",
        error,
      });
      const textareaRect = textarea.getBoundingClientRect();
      const textareaStyle = window.getComputedStyle(textarea);

      // Fallback: calculate position based on text metrics
      const textBeforeWord = value.slice(0, matchPosition.start);
      const mirrorDiv = document.createElement("div");
      mirrorDiv.style.position = "absolute";
      mirrorDiv.style.visibility = "hidden";
      mirrorDiv.style.whiteSpace = "pre-wrap";
      mirrorDiv.style.wordWrap = "break-word";
      mirrorDiv.style.overflowWrap = "break-word";
      mirrorDiv.style.font = textareaStyle.font;
      mirrorDiv.style.fontSize = textareaStyle.fontSize;
      mirrorDiv.style.fontFamily = textareaStyle.fontFamily;
      mirrorDiv.style.fontWeight = textareaStyle.fontWeight;
      mirrorDiv.style.lineHeight = textareaStyle.lineHeight;
      mirrorDiv.style.letterSpacing = textareaStyle.letterSpacing;
      mirrorDiv.style.padding = textareaStyle.padding;
      mirrorDiv.style.border = textareaStyle.border;
      mirrorDiv.style.boxSizing = textareaStyle.boxSizing;
      mirrorDiv.style.width = `${textarea.offsetWidth}px`;
      mirrorDiv.textContent = textBeforeWord;
      document.body.appendChild(mirrorDiv);

      const beforeWidth = mirrorDiv.offsetWidth;
      const beforeHeight = mirrorDiv.offsetHeight;
      document.body.removeChild(mirrorDiv);

      const paddingTop = parseFloat(textareaStyle.paddingTop) || 0;
      const paddingLeft = parseFloat(textareaStyle.paddingLeft) || 0;

      const rawTop = textareaRect.top + paddingTop + beforeHeight + 4;
      const rawLeft = textareaRect.left + paddingLeft + beforeWidth;
      const approxWordH = parseFloat(textareaStyle.lineHeight) || 20;
      const anchorBottom = rawTop - 4;
      return clampSynonymPopoverToViewport(
        { top: rawTop, left: rawLeft },
        {
          top: anchorBottom - approxWordH,
          left: rawLeft,
          bottom: anchorBottom,
          right: rawLeft + 120,
        },
        { maxPopoverHeight: estimateSynonymPopoverHeight(synonymMatches.length) }
      );
    }
  }, [value, matchPosition, synonymMatches.length]);

  // Debounced matching function
  const performMatchingRef = useRef(
    debounce(
      (
        text: string,
        cursorPos: number,
        dictionary: typeof synonymDictionary,
        preferences: typeof synonymPreferencesQuery.data
      ) => {
        // Only match if synonym matching is active
        if (!preferences?.synonymMatchingActive) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        const { start, end, word } = findCurrentWord(text, cursorPos);

        // Don't show suggestions if word is empty or too short
        if (!word || word.length < 2) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        // Don't show suggestions if cursor is in the middle of a word (not at the end)
        if (cursorPos < end) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        const matches = findSynonymMatches(word, dictionary);

        if (matches.length > 0) {
          setSynonymMatches(matches);
          setMatchPosition({ start, end });
          setIsSuggestionsOpen(true);
        } else {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
        }
      },
      150
    )
  );

  const performMatching = useCallback(
    (text: string, cursorPos: number) => {
      performMatchingRef.current(
        text,
        cursorPos,
        synonymDictionary,
        synonymPreferencesQuery.data
      );
    },
    [synonymDictionary, synonymPreferencesQuery.data]
  );

  // Update suggestion position when match position changes
  useEffect(() => {
    if (isSuggestionsOpen && matchPosition) {
      const position = calculateSuggestionPosition();
      setSuggestionPosition(position);
    }
  }, [isSuggestionsOpen, matchPosition, calculateSuggestionPosition]);

  // Update position on scroll and resize
  useEffect(() => {
    if (!isSuggestionsOpen || !matchPosition) {
      return;
    }

    const updatePosition = () => {
      const position = calculateSuggestionPosition();
      if (position) {
        setSuggestionPosition(position);
      }
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        textarea.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isSuggestionsOpen, matchPosition, calculateSuggestionPosition]);

  // Handle text and cursor changes
  useEffect(() => {
    if (value && cursorPosition >= 0) {
      performMatching(value, cursorPosition);
    } else {
      setIsSuggestionsOpen(false);
      setSynonymMatches([]);
      setMatchPosition(null);
    }
  }, [value, cursorPosition, performMatching]);

  // Cleanup debounce on unmount
  useEffect(() => {
    const debouncedFn = performMatchingRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  // Functions
  const handleSynonymSelect = useCallback(
    (canonicalTerm: string) => {
      if (!matchPosition) {
        return;
      }

      const before = value.slice(0, matchPosition.start);
      const after = value.slice(matchPosition.end);
      const newValue = before + canonicalTerm + after;
      const newCursorPosition = matchPosition.start + canonicalTerm.length;

      setValue(newValue);
      setCursorPosition(newCursorPosition);
      setIsSuggestionsOpen(false);
      setSynonymMatches([]);
      setMatchPosition(null);

      // Set cursor position in textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(
            newCursorPosition,
            newCursorPosition
          );
          textareaRef.current.focus();
        }
      }, 0);
    },
    [value, matchPosition]
  );

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newCursorPosition = e.target.selectionStart;
    setValue(newValue);
    setCursorPosition(newCursorPosition);
  };

  // Track selected index for keyboard navigation
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const handleTextareaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    // Handle keyboard navigation when suggestions are open
    if (isSuggestionsOpen && synonymMatches.length > 0) {
      // Intercept arrow keys to navigate suggestions
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const next = prev < synonymMatches.length - 1 ? prev + 1 : 0;
          return next;
        });
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : synonymMatches.length - 1;
          return next;
        });
        return;
      }

      // Handle Tab to select focused item
      if (e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
        if (synonymMatches[indexToSelect]) {
          handleSynonymSelect(synonymMatches[indexToSelect].canonicalTerm);
          setSelectedIndex(-1);
        }
        return;
      }

      // Close suggestions on Escape
      if (e.key === "Escape") {
        e.preventDefault();
        setIsSuggestionsOpen(false);
        setSelectedIndex(-1);
        return;
      }
    }

    // Handle Enter to submit (existing logic) - only when suggestions are closed
    if (e.key === "Enter" && !e.shiftKey && !isSuggestionsOpen) {
      e.preventDefault();
      if (disabled || value.trim() === "") return;
      handleSubmit();
    }
  };

  // Reset selected index when suggestions close or matches change
  useEffect(() => {
    if (!isSuggestionsOpen) {
      setSelectedIndex(-1);
    }
  }, [isSuggestionsOpen]);

  useEffect(() => {
    if (synonymMatches.length > 0 && isSuggestionsOpen) {
      setSelectedIndex(0); // Start with first item selected
    }
  }, [synonymMatches.length, isSuggestionsOpen]);

  const handleTextareaSelectionChange = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Measuring at height "auto" briefly makes the box tall enough for all of
      // its content, which an engine may take as a reason to clamp scrollTop to
      // 0 (Chromium does not). Restoring the offset keeps the height pass from
      // moving the user — notably right after the end was revealed (LAR-128).
      const scrollTop = textarea.scrollTop;
      textarea.style.height = "auto"; // Reset height to allow shrinking
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10);
      const maxHeight = lineHeight * MAX_INPUT_ROWS;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "scroll" : "hidden";
      textarea.scrollTop = scrollTop;
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      adjustTextareaHeight();
    });

    if (chatInputRef.current) {
      resizeObserver.observe(chatInputRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [value]);

  // Text set from outside the input starts scrolled to the bottom instead of
  // the top (LAR-128). Grow the textarea first so the scroll lands on the final
  // height, and do it before paint so there is no visible jump.
  useLayoutEffect(() => {
    if (revealEndRequest === 0) {
      return;
    }
    adjustTextareaHeight();
    revealTextareaEnd(textareaRef.current);
  }, [revealEndRequest]);

  const handleSubmit = () => {
    const tempInputText = value;
    if (!disabled && !waitingForResponse && connectionState === "Connected") {
      // Check if submission should be pending (e.g., waiting for warning dialog)
      const isPending = onPendingSubmit?.(tempInputText) ?? false;

      // Only clear input if submission is not pending
      if (!isPending) {
        setValue("");
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto"; // Reset height after submission
        }
      }

      onSubmit(tempInputText);
    } else {
      setIsShaking(true);

      setTimeout(() => {
        setIsShaking(false);
      }, 400);

      setValue(value.replace(/\n$/, ""));
    }
  };

  const handleConversationLanguageChange = (lang: ConversationLanguage) => {
    setSelectedLanguage(lang);
  };

  const handleChainOfThoughtClick = () => {
    setUseChainOfThought(!useChainOfThought);
  };

  const [inputHeight, setInputHeight] = useState<number | null>(null);
  useEffect(() => {
    if (textContainerRef.current?.clientHeight) {
      setInputHeight(textContainerRef.current.clientHeight);
    }
  }, []);

  return (
    <>
      <ConnectionStatus
        connectionState={connectionState}
        onReconnect={manualReconnect}
      />

      <div
        className={cn(
          "w-full rounded-xl flex flex-col border overflow-hidden",
          disabled && "opacity-60 cursor-not-allowed",
          isFocused &&
            !disabled &&
            "border-transparent ring-1 ring-input-focused"
        )}
        ref={chatInputRef}
      >
        <SelectedContext
          selectedConversation={selectedConversation}
          workspaces={workspaces}
          isLoadingWorkspaces={isLoadingWorkspaces}
        />
        <div className="p-0.5 relative">
          <div className="flex w-full flex-row">
            <div
              className="h-full flex items-center w-full px-2"
              ref={textContainerRef}
            >
              <textarea
                ref={textareaRef}
                value={value}
                placeholder={t("chat.inputPlaceholder")}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  // Close suggestions on blur (with slight delay to allow click)
                  setTimeout(() => {
                    setIsSuggestionsOpen(false);
                  }, 200);
                }}
                className={cn(
                  "chat-input",
                  "w-full bg-transparent text-sm resize-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
                  "outline-hidden focus:outline-hidden",
                  "border-none focus:border-none",
                  "ring-0 focus:ring-0",
                  " py-2",
                  isShaking && "animate-shake border-destructive"
                )}
                onChange={handleTextareaChange}
                onKeyDown={handleTextareaKeyDown}
                onSelect={handleTextareaSelectionChange}
                onClick={handleTextareaSelectionChange}
                rows={DEFAULT_INPUT_ROWS}
                style={{ lineHeight: "1.5" }}
                data-testid={inputTestId}
                disabled={disabled}
              />
              {isSuggestionsOpen && synonymMatches.length > 0 && (
                <SynonymSuggestions
                  matches={synonymMatches}
                  isOpen={isSuggestionsOpen}
                  onClose={() => {
                    setIsSuggestionsOpen(false);
                    setSelectedIndex(-1);
                  }}
                  onSelect={handleSynonymSelect}
                  anchorElement={textareaRef.current}
                  position={suggestionPosition || undefined}
                  selectedIndex={selectedIndex}
                />
              )}
            </div>
          </div>

          <TooltipProvider>
            <div
              className="flex flex-row items-center justify-between w-full px-2"
              style={{ height: inputHeight ? `${inputHeight}px` : undefined }}
            >
              <div className="flex flex-row items-center">
                <ConversationLanguageButton
                  selectedLanguage={selectedLanguage}
                  onChange={handleConversationLanguageChange}
                  disabled={disabled}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={() => !disabled && setPromptLibraryOpen(true)}
                      disabled={disabled}
                      className="select-none px-2.5 py-5"
                    >
                      <PromptLibraryIcon
                        size={20}
                        className={`prompt-library-icon w-6 ${
                          promptLibraryOpen ? "text-primary" : ""
                        }`}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Prompt-bibliotek</p>
                  </TooltipContent>
                </Tooltip>
                <LawDictionaryModal disabled={disabled} />

                <ChainOfThoughtButton
                  useChainOfThought={useChainOfThought}
                  handleChainOfThoughtClick={handleChainOfThoughtClick}
                  disabled={disabled}
                />
                <ToneOfVoicePopover disabled={disabled} />
              </div>
              <div className="flex gap-2 items-center size-8">
                <button
                  onClick={
                    disabled || isScanning || !value.trim()
                      ? undefined
                      : handleSubmit
                  }
                  disabled={disabled || isScanning || !value.trim()}
                  className={cn(
                    "rounded-md p-2 h-fit transition-all duration-300 ease-in-out",
                    disabled || !value.trim()
                      ? "opacity-50 bg-muted text-muted-foreground cursor-not-allowed"
                      : isScanning
                        ? "opacity-100 bg-primary text-primary-foreground cursor-wait"
                        : "opacity-100 bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90"
                  )}
                  data-testid={submitTestId}
                  type="button"
                >
                  {/* PII scan in flight: spinner instead of the send arrow (LAR-27 v2) */}
                  {isScanning ? (
                    <Loader2 className="size-4 animate-spin text-primary-foreground" />
                  ) : (
                    <ArrowUp
                      className={cn(
                        "size-4",
                        disabled || !value.trim()
                          ? "text-muted-foreground"
                          : "text-primary-foreground"
                      )}
                    />
                  )}
                </button>
              </div>
            </div>
          </TooltipProvider>
        </div>
      </div>
      <PromptLibraryDialog
        open={promptLibraryOpen}
        onOpenChange={setPromptLibraryOpen}
        onPromptSelect={(promptText) => {
          setInputValue(promptText);
          setPromptLibraryOpen(false);
        }}
      />
    </>
  );
};

export default ChatInput;
